import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Magnetic 3D tilt wrapper — usable around any card.
 * Tilts based on cursor position; glowing highlight tracks pointer.
 */
const Tilt = ({
    children,
    className = "",
    max = 8,
    scale = 1.02,
    glow = true,
}) => {
    const ref = useRef(null);
    const x = useMotionValue(0.5);
    const y = useMotionValue(0.5);
    const sx = useSpring(x, { stiffness: 200, damping: 25 });
    const sy = useSpring(y, { stiffness: 200, damping: 25 });
    const rotateX = useTransform(sy, [0, 1], [max, -max]);
    const rotateY = useTransform(sx, [0, 1], [-max, max]);
    const glowBg = useTransform(
        [sx, sy],
        ([gx, gy]) =>
            `radial-gradient(400px circle at ${gx * 100}% ${gy * 100}%, rgba(0,229,255,0.18), transparent 40%)`
    );

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        x.set((e.clientX - r.left) / r.width);
        y.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => {
        x.set(0.5);
        y.set(0.5);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            whileHover={{ scale }}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            transition={{ type: "spring", stiffness: 250, damping: 22 }}
            className={`relative will-change-transform ${className}`}
        >
            {children}
            {glow && (
                <motion.div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 hover:opacity-100 transition-opacity"
                    style={{ background: glowBg, mixBlendMode: "screen" }}
                />
            )}
        </motion.div>
    );
};

export default Tilt;
